/** Chunk-safe SSE framing. Tokens and payloads are never persisted or logged. */
export function marketEventParser(onEvent: (event: string) => void) {
  let buffer = "";
  return (chunk: string) => {
    buffer += chunk;
    if (buffer.length > 65536) throw new Error("market_stream_invalid");
    for (;;) {
      const boundary = /\r?\n\r?\n/.exec(buffer);
      if (boundary?.index === undefined) break;
      const message = buffer.slice(0, boundary.index);
      buffer = buffer.slice(boundary.index + boundary[0].length);
      const event = /^event:\s*([^\r\n]+)$/m.exec(message)?.[1]?.trim();
      if (event) onEvent(event);
    }
  };
}

export function watchMarketStream(connect: (signal: AbortSignal) => Promise<Response>,
  onChange: () => void, onState: (state: "live" | "reconnecting") => void, namespace = "market") {
  let stopped = false, running = false, retry = 2000;
  let controller: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const start = async () => {
    if (stopped || document.hidden || running) return;
    running = true;
    controller = new AbortController();
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    const touch = () => { clearTimeout(watchdog); watchdog = setTimeout(() => controller?.abort(), 45000); };
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
    try {
      touch();
      const response = await connect(controller.signal);
      if (!response.ok || !response.body || !response.headers.get("content-type")?.includes("text/event-stream")) throw new Error("market_stream_unavailable");
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      const parse = marketEventParser(event => {
        touch(); retry = 2000;
        if (!stopped && !document.hidden) {
          onState("live");
          if (event === `${namespace}.ready` || event === `${namespace}.changed`) onChange();
        }
      });
      while (!stopped && !controller.signal.aborted) {
        const next = await reader.read();
        if (next.done) break;
        parse(decoder.decode(next.value, { stream: true }));
      }
    } catch { /* Reconnect automatically; never repeat a wallet payment. */ }
    finally {
      clearTimeout(watchdog); await reader?.cancel().catch(() => { /* The stream may already be closed. */ }); reader?.releaseLock();
      running = false;
      if (!stopped) {
        if (!document.hidden) onState("reconnecting");
        timer = setTimeout(() => { void start(); }, retry);
        retry = Math.min(30000, retry * 2);
      }
    }
  };
  const visibility = () => {
    clearTimeout(timer);
    if (document.hidden) controller?.abort();
    else { retry = 2000; void start(); }
  };
  document.addEventListener("visibilitychange", visibility);
  window.addEventListener("online", visibility);
  void start();
  return () => { stopped = true; clearTimeout(timer); controller?.abort();
    document.removeEventListener("visibilitychange", visibility); window.removeEventListener("online", visibility); };
}
